import { useState } from 'react';
import styled from '@emotion/styled';

import type { OrganizationId } from '@hotjar/organization';
import { useOrganizations } from '@hotjar/organization';
import { useCurrentUser } from '@hotjar/user';
import { Select, Loader, tokens, PageHeader, Text, Field, Link, Divider } from '@hotjar/hotsauce';
import { ErrorBoundaryForPages } from '@hotjar/error-handler';
import { Page } from '@hotjar/webapp/components/Page';
import { useHelpHotjarUrl, useTranslation } from '@hotjar/translations';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';
import { trackClick, utils } from '@hotjar/tracking';
import { useCurrentSite } from '@hotjar/site';
import { RequestSection } from './RequestSection';

const PageBody = styled.div``;

const ContentWrapper = styled.div`
  padding: ${tokens.spacer.x3} 0;
  max-width: 740px;
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x3};
`;

export const VisitorLookupPage = (): JSX.Element => {
  const { t } = useTranslation('common');
  usePageTitle(`${t('visitor-lookup.page.title')} - Hotjar`);
  usePageTitle('User Lookup');
  const helpHotjarUrl = useHelpHotjarUrl();
  const { data: currentSite } = useCurrentSite();
  const { data: user, isLoading: isLoadingCurrentUser } = useCurrentUser();
  const { data: organizations = [], isLoading: isLoadingOrganizations } = useOrganizations(
    user?.id,
  );
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<OrganizationId>();
  const selectedOrganization = organizations.find((oneOrg) => oneOrg.id === selectedOrganizationId);

  return (
    <ErrorBoundaryForPages
      squadSentryTag="product-foundations"
      criticality="critical"
      featureName="setting.user.lookup"
    >
      <Page>
        <PageHeader title={t('visitor-lookup.page.title')} />
        <PageBody>
          <Loader isLoading={isLoadingCurrentUser || isLoadingOrganizations}>
            <ContentWrapper>
              <Text as="h2" textType="header5">
                {t('visitor-lookup.page.top-header')}
              </Text>
              <Text as="p" textType="body2">
                {t('visitor-lookup.page.top-description')}
                &nbsp;
                <Link
                  kind="inline"
                  as="a"
                  isExternal
                  href={`${helpHotjarUrl}/articles/360001749014`}
                  onClick={() => {
                    if (currentSite) {
                      trackClick({
                        name: 'clicked_learn_more_link_user_lookup',
                        uiPosition: 'learn_more_link_page_description',
                        groupData: utils.extractSiteGroupingProps(currentSite),
                        data: {
                          permission_level: utils.extractPermissionLevel(currentSite),
                        },
                      });
                    }
                  }}
                >
                  {t('visitor-lookup.page.top-link')}
                </Link>
              </Text>

              <Field inputId="" labelText={t('visitor-lookup.page.select-label').toUpperCase()}>
                <Select
                  id="organization"
                  name="organization"
                  data-testid="organization-select"
                  value={selectedOrganizationId}
                  onChange={(event) => {
                    if (event.target.value) {
                      setSelectedOrganizationId(+event.target.value as OrganizationId);
                    }
                  }}
                >
                  <option value="">{t('visitor-lookup.page.select-default-option')}</option>
                  {organizations.map((oneOrg) => (
                    <option value={oneOrg.id} key={oneOrg.id}>
                      {oneOrg.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Divider />
              {selectedOrganization && <RequestSection organization={selectedOrganization} />}
            </ContentWrapper>
          </Loader>
        </PageBody>
      </Page>
    </ErrorBoundaryForPages>
  );
};
